import {
  type BriefCard,
  type BriefNotSettled,
  type FollowUpNote,
  renderFollowUpText,
  renderRichText,
} from '@sigora/domain';
import { useId, useState } from 'react';

import {
  ASK_ABOUT_THIS_LABEL,
  BRIEF_HEADINGS,
  NOT_SETTLED_COPY,
  READ_LESS_LABEL,
  READ_MORE_LABEL,
  cardNoteLine,
} from '../brief-copy.ts';
import type { FollowUpScope } from '../follow-ups/FollowUpThread.tsx';
import { briefDate } from './brief-date.ts';
import { PollBar } from './PollBar.tsx';
import { ThemeStrip } from './ThemeStrip.tsx';

/**
 * One research question's card: the question, what the evidence says, the poll or themes behind
 * it, up to three quotes as the participants wrote them, and the detail behind "Read more". With
 * `onAsk` it also offers "Ask about this"; without, the card is read where nobody can ask.
 *
 * A card the study could not settle because nothing asked it, or nobody answered, opens on its
 * kind's own sentence rather than on the synthesis's headline, so the reason is said the same way
 * on every Brief.
 *
 * With `allThemeRows` its theme strip draws every row and no "+K more" (see `ThemeStrip`).
 *
 * Under its actions, the notes follow-ups added to the card, oldest first, each dated and with its
 * answer's quotes as the participants wrote them. A card with none has no place for them.
 */
export const EvidenceCard = ({
  card,
  notSettled,
  notes = [],
  onAsk,
  allThemeRows = false,
}: {
  card: BriefCard;
  /** The Not settled entry naming this card's research question, if there is one. */
  notSettled: BriefNotSettled | undefined;
  /** The notes follow-ups added under this card, oldest first. */
  notes?: readonly FollowUpNote[];
  /**
   * Ask about this: hands the card to the Brief's conversation, as what the next question is about.
   * Absent, the card offers none.
   */
  onAsk?: (card: FollowUpScope) => void;
  /** Every theme row drawn and no "+K more": a page nobody can press. */
  allThemeRows?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const detailId = useId();
  const forced =
    card.status === 'not_settled' && notSettled !== undefined && notSettled.kind !== 'unsettled'
      ? NOT_SETTLED_COPY[notSettled.kind]
      : null;

  return (
    <article
      className="brief-card"
      data-test-id="brief-card"
      data-status={card.status}
      data-rq-id={card.rq_id}
    >
      <h3 className="brief-card-rq" data-test-id="brief-card-rq">
        {card.rq_text}
      </h3>
      {card.status === 'not_settled' && (
        <p className="brief-card-status" data-test-id="brief-card-status">
          {BRIEF_HEADINGS.notSettled}
        </p>
      )}
      <p className="brief-card-headline" data-test-id="brief-card-headline">
        {forced ?? renderRichText(card.headline)}
      </p>

      {card.visual?.kind === 'poll' && <PollBar poll={card.visual} />}
      {card.visual?.kind === 'themes' && (
        // Keyed on its rows, so a revision that changes them opens on the first rows again.
        <ThemeStrip
          key={card.visual.themes.map((theme) => theme.theme_id).join(' ')}
          visual={card.visual}
          allRows={allThemeRows}
        />
      )}

      {card.quotes.map((quote) => (
        <blockquote key={quote.quote_id} className="brief-quote" data-test-id="brief-card-quote">
          <p data-test-id="brief-card-quote-text">{quote.text}</p>
          <footer className="brief-meta" data-test-id="brief-card-quote-participant">
            {quote.participant}
          </footer>
        </blockquote>
      ))}

      {/* In the page while hidden, so the toggle's `aria-controls` always names an element. */}
      <p id={detailId} data-test-id="brief-card-detail" hidden={!open}>
        {renderRichText(card.detail)}
      </p>

      <div className="brief-card-actions">
        <button
          type="button"
          data-test-id="brief-card-detail-toggle"
          aria-expanded={open}
          aria-controls={detailId}
          onClick={() => setOpen((shown) => !shown)}
        >
          {open ? READ_LESS_LABEL : READ_MORE_LABEL}
        </button>
        {onAsk !== undefined && (
          <button
            type="button"
            data-test-id="brief-card-ask"
            onClick={() => onAsk({ rqId: card.rq_id, label: card.rq_text })}
          >
            {ASK_ABOUT_THIS_LABEL}
          </button>
        )}
      </div>

      {notes.map((note) => (
        <div key={note.followUpId} className="brief-card-note" data-test-id="brief-card-note">
          <p data-test-id="brief-card-note-text">
            {cardNoteLine(briefDate(note.finalizedAt), renderFollowUpText(note.text))}
          </p>
          {note.quotes.map((quote) => (
            <blockquote
              key={quote.quote_id}
              className="brief-quote"
              data-test-id="brief-card-note-quote"
            >
              <p data-test-id="brief-card-note-quote-text">{quote.text}</p>
              <footer className="brief-meta" data-test-id="brief-card-note-quote-participant">
                {quote.participant}
              </footer>
            </blockquote>
          ))}
        </div>
      ))}
    </article>
  );
};
